import { useId, useMemo, useState } from 'react';
import { useAuth } from '@/lib/use-auth';
import { ContactFormModal } from '@/components/contacts/ContactFormModal';
import {
  CONTACT_ADD_NEW_LABEL,
  contactAddNewOffer,
  type ContactScope,
} from '@/lib/contact-scope';
import { Combobox, Field } from '@/components/ui';
import type { ComboboxOption } from '@/components/ui';

export interface ContactPickerFieldProps {
  /** The selected contact id as a string; `''` is Unassigned. */
  value: string;
  onChange: (value: string) => void;
  /**
   * The scoped options, already carrying the pinned contact and — where the
   * caller judged the list authoritative — its `CONTACT_NOT_ON_COMPANY` mark.
   * Anything created here is merged on top; nothing else is added.
   */
  options: ComboboxOption[];
  onSearch: (term: string) => void;
  loading: boolean;
  footnote?: string;
  error?: string;
  /** What the field knows about its own list — `useLookupSearch`'s `scope`. */
  scope: ContactScope;
  /**
   * The company the document names, **with its name**, or `null` when it names
   * none or the name has not landed yet.
   *
   * ⚠️ The name is not decoration: `ContactFormModal` shows the account as a
   * read-only Input in locked mode rather than looking it up, so an id with no
   * name cannot be locked — and an id with no name is why
   * `contactAddNewOffer` withholds the row rather than opening a form whose
   * Account box would be blank.
   */
  company: { id: number; name: string } | null;
}

/**
 * The Contact field on a deal, a quote and an invoice — the picker and the
 * inline "+ Add a new contact…" row behind it.
 *
 * ⚠️ *SUPERSEDED 2026-09-05: "the picker, its hint, and the inline row".* The
 * hint went with the owner's stricter helper-text ruling; see the note in the
 * body. The sentence is corrected here rather than left, because a doc comment
 * naming a surface that no longer renders sends the next reader looking for it.
 *
 * ONE component for the three forms, for exactly the reason `contact-scope.ts`
 * is one module for their copy: the three call sites were byte-identical, and
 * three copies of a control that decides whether a person may be created
 * against the wrong account would agree the day they were written and drift on
 * the first correction. Every *decision* it makes lives in `contact-scope.ts`;
 * this file is the rendering.
 *
 * ⚠️ **The row locks the company, and that is the whole point of it.** A
 * contact created here belongs to the account the document is already for —
 * `contacts.service.ts` checks a contact for EXISTENCE and never against the
 * company, so a person raised against the wrong account is accepted with a
 * `200` and nothing anywhere flags it. Letting somebody pick a different company
 * from inside this form would manufacture exactly the mismatch the scoping was
 * built to remove.
 */
export function ContactPickerField({
  value,
  onChange,
  options,
  onSearch,
  loading,
  footnote,
  error,
  scope,
  company,
}: ContactPickerFieldProps) {
  const { ability } = useAuth();
  const [creating, setCreating] = useState(false);
  /**
   * Contacts created from this row, so a new one reads as a NAME the instant it
   * is selected rather than as an id the picker cannot resolve. Same answer as
   * `ProjectFormModal`'s `createdCities`, and the same reason: nothing refetches
   * the scoped list, so without this the chip would sit blank until the form
   * were reopened.
   *
   * ⚠️ Carries the company it was created FOR. The scoped list drops the
   * previous company's rows on a company change rather than showing them dimmed
   * — a created contact must follow the same rule, or moving the document to
   * another account would leave a person from the old one offered under the new
   * one's name.
   */
  const [created, setCreated] = useState<{ companyId: number; id: number; name: string }[]>([]);

  /*
   * ⚠️ `contactScopeHint(scope)` is NO LONGER RENDERED — owner's stricter
   * helper-text ruling, 2026-09-05. It resolved to one of four sentences: no
   * company chosen yet, this company's contacts could not be read, this company
   * has no contacts, and the contact was cleared because the company changed.
   * None is a server refusal, so none clears the bar.
   *
   * ⚠️ **The FUNCTION is deliberately left in `src/lib/contact-scope.ts` and is
   * NOT deleted here** — that module is outside this sweep's slice and is shared
   * with the deal, quote and invoice forms, which are other people's files. If
   * those three drop it too it becomes dead and can go in one place; removing it
   * from under them now would break their build.
   *
   * ⚠️ The `scope` prop STAYS and is still load-bearing: `contactAddNewOffer`
   * below reads it to decide whether the "+ Add a new contact…" row may be
   * offered at all, and that decision is unchanged.
   */
  const offer = contactAddNewOffer({
    scope,
    // Contacts are ordinary CRM records — `basic_user` and above may create
    // them. Never `user.role`, never `/permissions/me`.
    canCreate: ability.canCreate('contacts'),
    company,
  });

  /*
   * ⚠️ Not in `hint`: `Field` drops `hint` the moment an `error` lands, so a
   * validation error on the contact would silently swallow the reason the add
   * row is missing too. It renders as its own element, wired into
   * `aria-describedby` by hand — `PickerField` reaches for the same answer, for
   * the same reason.
   */
  const reasonId = `${useId()}-contact-add-new`;
  const reason = offer.offered ? null : offer.reason;

  const merged = useMemo<ComboboxOption[]>(() => {
    if (created.length === 0 || scope.companyId === null) return options;
    const extra = created
      .filter((row) => row.companyId === scope.companyId)
      .filter((row) => !options.some((option) => option.value === String(row.id)))
      .map((row) => ({ value: String(row.id), label: row.name }));
    return extra.length === 0 ? options : [...options, ...extra];
  }, [options, created, scope.companyId]);

  return (
    <>
      <Field label="Contact" error={error}>
        {({ id, 'aria-describedby': describedBy }) => (
          <Combobox
            id={id}
            value={value}
            onChange={onChange}
            options={merged}
            onSearch={onSearch}
            loading={loading}
            emptyOptionLabel="Unassigned"
            searchPlaceholder="Search this company’s contacts…"
            footnote={footnote}
            invalid={Boolean(error)}
            addNew={
              offer.offered
                ? { label: CONTACT_ADD_NEW_LABEL, onSelect: () => setCreating(true) }
                : undefined
            }
            aria-describedby={
              [describedBy, reason === null ? null : reasonId].filter(Boolean).join(' ') || undefined
            }
          />
        )}
      </Field>

      {/* `text-warning`, not `text-danger`: nothing the person did is wrong, and
          a danger tone beside a real validation error would make the two
          indistinguishable. */}
      {reason !== null && (
        <p id={reasonId} className="text-xs break-words text-warning">
          {reason}
        </p>
      )}

      {/*
        * Mounted only while creating, so the contact form's own fetches — the
        * staff directory behind its lead pickers — are not paid for on every
        * deal, quote and invoice form that is merely opened.
        *
        * ⚠️ *SUPERSEDED: "The limiter is 120 requests per 60s per IP, shared
        * across the office."* It is **12,000/60s** (corrected 2026-09-17).
        * Mounting only while creating is still right — those are requests
        * nothing on screen can use — but the arithmetic that reason cited is
        * off by a factor of a hundred.
        *
        * ⚠️ `offer.company` rather than `company`: it is the same object, but
        * reading it off the OFFER makes the lock and the decision to show the
        * row structurally the same fact. There is no branch here in which a row
        * was offered and the company it locks to is unknown.
        */}
      {creating && offer.offered && (
        <ContactFormModal
          open
          lockedCompany={offer.company}
          onClose={() => setCreating(false)}
          onSaved={(contact) => {
            setCreated((prev) => [
              ...prev,
              { companyId: offer.company.id, id: contact.id, name: contact.name },
            ]);
            /*
             * Selected through the caller's own handler, which is what sets the
             * form's "the contact was chosen deliberately" flag — creating a
             * person for this document and then not naming them on it is not a
             * state anybody asked for.
             */
            onChange(String(contact.id));
            setCreating(false);
          }}
        />
      )}
    </>
  );
}
